/* eslint-disable react/prop-types */
// Polyfills for IE11 + stable
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import 'custom-event-polyfill';
import 'abort-controller/polyfill.js';

import * as Sentry from '@sentry/browser';
import ReactGA from 'react-ga4';
import {hotjar} from 'react-hotjar';
import svg4everybody from 'svg4everybody';
import moment from 'moment-timezone';
import {Provider} from 'react-redux';
import {ConnectedRouter} from 'connected-react-router';
import {Switch, Route, BrowserRouter} from 'react-router-dom';
import {ThemeProvider} from '@material-ui/core/styles';
import {theme} from 'front-onceforall-ui-kit/theme-fo.js';
import {isEmpty} from 'front-onceforall-core/dist/utils/arrays';
import ReactDOM from 'react-dom';
import history from './app/history/history';
import UserRegisterView from './app/views/userRegister/UserRegisterView';
import UserUpdateEmailView from './app/views/userUpdateEmail/UserUpdateEmailView';
import AuthenticationGate from './app/gates/AuthenticationGate';
import PageChange from './app/layout/PageChange';
import maintenanceService from './app/services/maintenance/MaintenanceService';
import {setMaintenance} from './app/actions/maintenance';
import configureStore from './app/store/configureStore';
import {captureError} from './app/utils/log';
import {recordGAReloadEvent} from './app/utils/analytics';
import SpaPropsProvider from './app/SpaPropsProvider';
import reportWebVitals from './reportWebVitals';
import config from './config';

// Used to allow IE11 to handle svg use with external ressource href
svg4everybody();

configureStore.init(history);
const store = configureStore.getStore();
if (config.sentry) {
  const release = `${process.env.REACT_APP_NAME}@${process.env.REACT_APP_VERSION}`;
  Sentry.init({
    dsn: config.sentry.url,
    environment: config.sentry.environment,
    release
  });
}

if (config.titlePrefix) {
  document.title = `${config.titlePrefix} ${document.title}`;
}

if (!config.isMicrofront) {
  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/png';
  link.href = `/favicons/${config.favicon || 'favicon.png'}`;
  document.head.appendChild(link);
}

// Analytics
const gaIds = [];
if (config.gtmId) {
  gaIds.push({trackingId: config.gtmId});
}
if (config.analytics) {
  gaIds.push({trackingId: config.analytics});
}
if (!isEmpty(gaIds)) {
  ReactGA.initialize(gaIds);
}
// Handle GA Reload event
if (config.analytics) {
  const pageAccessedByReload = (
    (window.performance.navigation && window.performance.navigation.type === 1) ||
    window.performance
      .getEntriesByType('navigation')
      .map(nav => nav.type)
      .includes('reload')
  );

  if (pageAccessedByReload) {
    recordGAReloadEvent(window.location.pathname);
  }
}

if (config.hotjar) {
  hotjar.initialize(config.hotjar.id, config.hotjar.version);
}

function checkMaintenanceDate() {
  try {
    maintenanceService.get()
      .then(response => {
        if (!response || !response.fo) {
          return;
        }

        const maintenance = response.fo;
        store.dispatch(setMaintenance(maintenance));
        let maintenanceDate = maintenance['maintenance-date'];
        if (maintenanceDate) {
          maintenanceDate = moment(maintenanceDate);
          if (!maintenanceDate.isValid()) {
            captureError('maintenance date is invalid !');
          } else if (maintenanceDate.isBefore(moment())) {
            window.location = maintenance.maintenanceUrl;
          }
        }
      })
      .catch(error => captureError('maintenanceService.get', error));

    // Every minute, we check again
    setTimeout(checkMaintenanceDate, 60000);
  } catch (error) {
    captureError('An error occurred during the initialization of the checkMaintenanceDate routine');
  }
}

checkMaintenanceDate();

function Root(props) {
  const basePath = (props && props.basePath) || '';

  return (
    <SpaPropsProvider spaProps={props}>
      <Provider store={store}>
        <BrowserRouter basename={`/${basePath}`}>
          <ConnectedRouter history={history}>
            <ThemeProvider theme={theme}>
              <PageChange>
                <Switch>
                  <Route
                    exact
                    path="/register-user"
                    component={UserRegisterView}
                  />
                  <Route
                    exact
                    path="/update-email"
                    component={UserUpdateEmailView}
                  />
                  <Route render={() => <AuthenticationGate store={store}/>}/>
                </Switch>
              </PageChange>
            </ThemeProvider>
          </ConnectedRouter>
        </BrowserRouter>
      </Provider>
    </SpaPropsProvider>
  );
}

if (!config.isMicrofront) {
  ReactDOM.render(
    Root(),
    document.getElementById('root')
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

export default Root;
